import { useBusinessInfoContext } from 'contexts/BusinessContextProvider';
import useChangePage from 'hooks/useChangePage';
import { useTrialsContext } from 'contexts/TrialsContextProvider';
import { getCookies } from 'lib/getCookies';
import { TRIAL_URL } from 'routes/constants';

import { onSuccessfulCreateTrial, redirectToOnboarding } from '../utils';

import useCheckResourcesReady from './useCheckResourcesReady';
import useCreateTrial from './useCreateTrial';
import useCreateTrialNoCard from './useCreateTrialNoCard';
import useTrialCreationVariables from './useTrialCreationVariables';

const useSubmitCreateTrial = () => {
  const { baseVariables, setTrialCreationError, trackEvent } =
    useTrialCreationVariables();
  const { address, gmb } = useBusinessInfoContext() || {};
  const changePage = useChangePage();
  const { handleCheckResources } = useCheckResourcesReady();
  const [createTrial] = useCreateTrial();
  const [createTrialNoCard] = useCreateTrialNoCard();
  const {
    email,
    firstName,
    firstUtmMedium,
    googleClientId,
    lastName,
    partnerId,
    password,
    salesforceCampaignId,
    setIsSubmittingTrial: setIsSubmitting,
    trialPlan = {},
    utmMedium,
    showDiscountPricing,
    ssqt,
    fbclid,
    msclkid,
    googleClickId,
  } = useTrialsContext() || {};
  const { plan } = trialPlan;

  const setError = (error, isStripeError = false) => {
    setTrialCreationError(error, isStripeError);

    setIsSubmitting(false);
  };

  const handleCreateTrialResponse = ({
    errors,
    isFreeTrialStart = true,
    responseData = {},
    redirect = redirectToOnboarding,
  } = {}) => {
    if (!errors) {
      const locationUid = responseData.organization?.locations[0].uid;
      const handleRedirect = () => {
        showDiscountPricing
          ? changePage(`${TRIAL_URL}/buy-now`)
          : redirect(locationUid);
      };

      onSuccessfulCreateTrial({
        email,
        firstName,
        idToken: responseData.idToken,
        isFreeTrialStart,
        lastName,
        locationUid,
        noPasswordSet: !password,
        organizationUid: responseData.organization?.uid,
        plan,
        refreshToken: responseData.refreshToken,
        userUid: responseData.userUid,
      });

      trackEvent('trials.startTrial.success');

      handleCheckResources(
        {
          organizationUid: responseData.organization?.uid,
          locationUid,
        },
        handleRedirect
      );
    }
  };

  const createNewTrialNoCreditCard = async (
    validGmb = false,
    redirect = redirectToOnboarding
  ) => {
    let input = {
      ...(validGmb && { address }),
      businessName: baseVariables.businessName,
      email: baseVariables.email,
      firstName: baseVariables.firstName,
      firstUtmMedium,
      googleClientId,
      googlePlaceId: gmb?.placeId || null,
      lastName: baseVariables.lastName,
      partnerId,
      password,
      phone: baseVariables.phone,
      salesforceCampaignId,
      sessionId: getCookies().podiumSessionId,
      googleClickId,
      fbclid,
      msclkid,
      tosAccepted: true,
      utmMedium,
      ssqt,
    };

    try {
      const {
        data: { createAccountWithNoCreditCardTrial: trialData },
        errors,
      } = await createTrialNoCard({
        context: {
          headers: {
            'x-ipqs-id': window.ipqs,
          },
        },
        variables: { input },
      });

      handleCreateTrialResponse({ errors, responseData: trialData, redirect });
    } catch (error) {
      setError(error);
    }
  };

  const createNewTrial = async ({
    stripeCustomerId,
    stripePaymentMethodId,
    stripeSetupIntentId,
    incompleteGmb = false,
  }) => {
    try {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const {
        data: { createTrial: trialData },
        errors,
      } = await createTrial({
        context: {
          headers: {
            'x-ipqs-id': window.ipqs,
          },
        },
        variables: {
          ...baseVariables,
          address: incompleteGmb ? {} : address,
          currentTrialSlug: plan,
          firstUtmMedium,
          googleClientId,
          googlePlaceId: gmb?.placeId || null,
          partnerId,
          password,
          salesforceCampaignId,
          sessionId: getCookies().podiumSessionId,
          stripeCustomerId,
          stripePaymentMethodId,
          stripeSetupIntentId,
          tosAccepted: true,
          userTimezone,
          utmMedium,
        },
      });

      handleCreateTrialResponse({
        errors,
        isFreeTrialStart: false,
        responseData: trialData,
      });
    } catch (error) {
      setError(error);
    }
  };

  return { createNewTrial, createNewTrialNoCreditCard };
};

export default useSubmitCreateTrial;
